import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, SlidersHorizontal, XCircle, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { fmtEUR, L } from "@/constants/labels";
import { Modal, Motif } from "./Modal";

const T = L.versements;

/* Hiérarchie visuelle volontairement nette :
   - « Je valide » : bouton plein navy, seul bouton de cette couleur, avec étape de confirmation.
   - « Je valide partiellement » : bouton contour orange, ouvre la saisie du pourcentage.
   - « Je conteste » : lien texte rouge, jamais un bouton plein. */

const useGo = (fn, onDone) => {
  const [busy, setBusy] = useState(false);
  return [busy, async () => { setBusy(true); try { await fn(); onDone(); } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); } }];
};

const ConfirmValidation = ({ montant, label, onCancel, onConfirm, busy }) => (
  <div className="border-l-4 border-[#1B3A5C] bg-[#EEF3F8] p-3 space-y-2" data-testid="decision-confirm-box">
    <p className="font-semibold text-[#1F2933] inline-flex items-center gap-1"><AlertTriangle size={14} /> Confirmer la validation</p>
    <p>Vous validez <b>{fmtEUR(montant)}</b> HT{label ? ` (${label})` : ""}. Ce montant passera en « validé, en attente de libération » : cette décision est horodatée et engage un paiement.</p>
    <div className="flex gap-2 justify-end">
      <button className="btn-ghost" onClick={onCancel} data-testid="decision-confirm-cancel">Revenir</button>
      <button className="btn-primary" disabled={busy} onClick={onConfirm} data-testid="decision-confirm-ok"><CheckCircle2 size={14} /> {busy ? "…" : "Oui, je valide"}</button>
    </div>
  </div>
);

/** Acompte + situation : valide / partiel (situation) / conteste. */
export const DecisionModal = ({ v, chaine, onClose, onDone }) => {
  const [mode, setMode] = useState(null); // null | confirm | partiel | conteste
  const [pct, setPct] = useState("");
  const [motif, setMotif] = useState("");
  const isSit = v.type === "situation";
  const montantPartiel = pct ? (chaine.commande_ht * Number(pct)) / 100 : 0;
  const [busy, go] = useGo(async () => {
    const body = mode === "confirm" ? { decision: "valide" } : mode === "partiel" ? { decision: "partiel", pct_constate: Number(pct), motif } : { decision: "conteste", motif };
    await api.post(`/versements/${v.id}/decision`, body);
    toast.success(mode === "confirm" ? "Validé — en attente de libération" : mode === "partiel" ? "Validation partielle transmise à l'artisan" : "Contestation transmise — rien n'est validé");
  }, onDone);
  const title = isSit ? `Situation — ${v.pct_demande} % demandés` : "Démarrage du chantier déclaré";
  return (
    <Modal title={title} onClose={onClose} testid="decision-modal">
      <div className="bg-[#F5F6F7] rounded p-3 text-xs space-y-1">
        <div>Montant demandé : <b className="text-base">{fmtEUR(v.montant_demande_ht)}</b> HT{isSit && <> · {v.pct_demande} % de la commande</>}</div>
        {isSit && <div>Cumul déjà validé : {v.declaration?.cumul_valide_pct_avant ?? chaine.cumul_valide_pct} %</div>}
        {v.type === "acompte" && <div>Démarrage déclaré le {v.declaration?.date_demarrage}</div>}
        {v.declaration?.commentaire && <div className="italic">« {v.declaration.commentaire} »</div>}
      </div>
      <p className="text-xs text-[#3E4C59]">{T.aucuneLiberation}</p>

      {mode === null && (
        <div className="space-y-3 pt-1">
          <button className="btn-primary w-full py-3 text-base justify-center" onClick={() => setMode("confirm")} data-testid="decision-valide-btn">
            <CheckCircle2 size={18} /> {isSit ? `${T.valider} — ${v.pct_demande} %` : T.confirmerDemarrage}
          </button>
          {isSit && (
            <button className="w-full py-2.5 rounded-[4px] border-2 border-[#E8710A] text-[#E8710A] bg-white hover:bg-[#FEF5EA] font-medium inline-flex items-center justify-center gap-2" onClick={() => setMode("partiel")} data-testid="decision-partiel-btn">
              <SlidersHorizontal size={16} /> {T.validerPartiel} — je saisis le % constaté
            </button>
          )}
          <div className="text-center pt-1">
            <button className="text-[#C0392B] underline text-sm inline-flex items-center gap-1" onClick={() => setMode("conteste")} data-testid="decision-conteste-btn"><XCircle size={14} /> {T.contester} (motif obligatoire)</button>
          </div>
        </div>
      )}

      {mode === "confirm" && <ConfirmValidation montant={v.montant_demande_ht} label={isSit ? `${v.pct_demande} %` : "acompte"} onCancel={() => setMode(null)} onConfirm={go} busy={busy} />}

      {mode === "partiel" && (
        <div className="border-l-4 border-[#E8710A] bg-[#FEF5EA] p-3 space-y-2" data-testid="decision-partiel-box">
          <p className="font-semibold text-[#1F2933]">Pourcentage constaté</p>
          <input type="number" min="0.5" max={v.pct_demande - 0.5} step="0.5" className="input text-lg" value={pct} onChange={(e) => setPct(e.target.value)} placeholder={`< ${v.pct_demande}`} data-testid="decision-partiel-pct" />
          {pct && <div className="text-xs">= <b>{fmtEUR(montantPartiel)}</b> HT au lieu de {fmtEUR(v.montant_demande_ht)}. L'artisan pourra accepter ou rouvrir la discussion.</div>}
          <Motif value={motif} onChange={setMotif} placeholder="Ce que vous constatez sur le chantier…" testid="decision-partiel-motif" />
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={() => setMode(null)}>Revenir</button>
            <button className="btn-accent" disabled={busy || !pct || Number(pct) >= v.pct_demande || Number(pct) <= 0 || motif.trim().length < 3} onClick={go} data-testid="decision-partiel-submit">{busy ? "…" : "Valider partiellement"}</button>
          </div>
        </div>
      )}

      {mode === "conteste" && (
        <div className="border-l-4 border-[#C0392B] bg-[#FEF2F2] p-3 space-y-2" data-testid="decision-conteste-box">
          <p className="font-semibold text-[#1F2933]">Contester — rien ne sera validé</p>
          <Motif value={motif} onChange={setMotif} placeholder="Motif transmis à l'artisan…" testid="decision-conteste-motif" />
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={() => setMode(null)}>Revenir</button>
            <button className="btn-danger" disabled={busy || motif.trim().length < 3} onClick={go} data-testid="decision-conteste-submit">{busy ? "…" : "Confirmer la contestation"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
};

/** Solde : réception sans réserve / avec réserves / refus. */
export const ReceptionModal = ({ v, chaine, onClose, onDone }) => {
  const [mode, setMode] = useState(null);
  const [reserves, setReserves] = useState([{ description: "" }]);
  const [motif, setMotif] = useState("");
  const r = chaine.retenue || {};
  const retenue = r.active ? r.montant_ht : 0;
  const [busy, go] = useGo(async () => {
    const body = mode === "sans" ? { decision: "sans_reserve" } : mode === "avec" ? { decision: "avec_reserves", reserves: reserves.filter((x) => x.description.trim()).map((x) => ({ description: x.description.trim() })) } : { decision: "refus", motif };
    await api.post(`/versements/${v.id}/reception`, body);
    toast.success(mode === "refus" ? "Réception refusée — le chantier reste ouvert" : "Réception prononcée — PV généré");
  }, onDone);
  const valides = reserves.filter((x) => x.description.trim().length >= 3);
  return (
    <Modal title="Réception des travaux" onClose={onClose} testid="reception-modal">
      <div className="bg-[#F5F6F7] rounded p-3 text-xs space-y-1">
        <div>Solde demandé : <b className="text-base">{fmtEUR(v.montant_demande_ht)}</b> HT</div>
        {r.active && <div>Retenue de garantie {r.taux} % = <b>{fmtEUR(retenue)}</b> HT, libérable dans {r.delai_mois} mois ou dès levée des réserves{r.levee_auto_sans_reserve ? " (levée automatiquement si réception sans réserve)" : ""}.</div>}
      </div>
      {mode === null && (
        <div className="space-y-3 pt-1">
          <button className="btn-primary w-full py-3 text-base justify-center" onClick={() => setMode("sans")} data-testid="reception-sans-btn"><CheckCircle2 size={18} /> {T.receptionSansReserve}</button>
          <button className="w-full py-2.5 rounded-[4px] border-2 border-[#E8710A] text-[#E8710A] bg-white hover:bg-[#FEF5EA] font-medium inline-flex items-center justify-center gap-2" onClick={() => setMode("avec")} data-testid="reception-avec-btn"><SlidersHorizontal size={16} /> {T.receptionAvecReserves}</button>
          <div className="text-center pt-1"><button className="text-[#C0392B] underline text-sm inline-flex items-center gap-1" onClick={() => setMode("refus")} data-testid="reception-refus-btn"><XCircle size={14} /> {T.refusReception} (motif obligatoire)</button></div>
        </div>
      )}
      {mode === "sans" && <ConfirmValidation montant={r.active && !r.levee_auto_sans_reserve ? v.montant_demande_ht - retenue : v.montant_demande_ht} label="solde, retenue déduite le cas échéant" onCancel={() => setMode(null)} onConfirm={go} busy={busy} />}
      {mode === "avec" && (
        <div className="border-l-4 border-[#E8710A] bg-[#FEF5EA] p-3 space-y-2" data-testid="reception-reserves-box">
          <p className="font-semibold text-[#1F2933]">Réserves constatées</p>
          {reserves.map((x, i) => (
            <div key={i} className="flex gap-2">
              <input className="input flex-1" value={x.description} onChange={(e) => setReserves(reserves.map((y, k) => k === i ? { description: e.target.value } : y))} placeholder={`Réserve ${i + 1}`} data-testid={`reserve-desc-${i}`} />
              {reserves.length > 1 && <button className="btn-ghost p-1" onClick={() => setReserves(reserves.filter((_, k) => k !== i))}><Trash2 size={14} /></button>}
            </div>
          ))}
          <button className="btn-outline text-xs" onClick={() => setReserves([...reserves, { description: "" }])} data-testid="reserve-add"><Plus size={12} /> Ajouter une réserve</button>
          <p className="text-xs">Solde validé : <b>{fmtEUR(v.montant_demande_ht - retenue)}</b> HT{retenue > 0 && <> (retenue {fmtEUR(retenue)} conservée jusqu'à levée des réserves)</>}.</p>
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={() => setMode(null)}>Revenir</button>
            <button className="btn-accent" disabled={busy || valides.length === 0} onClick={go} data-testid="reception-avec-submit">{busy ? "…" : `Réceptionner avec ${valides.length} réserve(s)`}</button>
          </div>
        </div>
      )}
      {mode === "refus" && (
        <div className="border-l-4 border-[#C0392B] bg-[#FEF2F2] p-3 space-y-2" data-testid="reception-refus-box">
          <p className="font-semibold text-[#1F2933]">Refuser la réception — le chantier reste ouvert</p>
          <Motif value={motif} onChange={setMotif} placeholder="Travaux inachevés, non-conformités…" testid="reception-refus-motif" />
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={() => setMode(null)}>Revenir</button>
            <button className="btn-danger" disabled={busy || motif.trim().length < 3} onClick={go} data-testid="reception-refus-submit">{busy ? "…" : "Confirmer le refus"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
};

/** Retenue : valider / contester la levée déclarée, ou libérer à l'échéance. */
export const LeveeDecisionModal = ({ v, onClose, onDone }) => {
  const [mode, setMode] = useState(null);
  const [motif, setMotif] = useState("");
  const declarees = v.reserves.filter((x) => x.statut === "levee_declaree");
  const [busy, go] = useGo(async () => {
    await api.post(`/versements/${v.id}/levee-decision`, mode === "valide" ? { decision: "valide" } : { decision: "conteste", motif });
    toast.success(mode === "valide" ? "Levée validée" : "Levée contestée — la retenue reste constituée");
  }, onDone);
  return (
    <Modal title="Levée des réserves déclarée" onClose={onClose} testid="levee-decision-modal">
      <ul className="space-y-1">{declarees.map((x) => <li key={x.id} className="p-2 bg-[#F5F6F7] rounded">{x.description}</li>)}</ul>
      <p className="text-xs text-[#3E4C59]">Retenue {fmtEUR(v.montant_demande_ht)} HT — validée « en attente de libération » quand toutes les réserves sont levées.</p>
      {mode === null && (
        <div className="space-y-3">
          <button className="btn-primary w-full py-3 justify-center" onClick={() => setMode("valide")} data-testid="levee-valide-btn"><CheckCircle2 size={18} /> Je valide la levée</button>
          <div className="text-center"><button className="text-[#C0392B] underline text-sm" onClick={() => setMode("conteste")} data-testid="levee-conteste-btn">Je conteste (motif obligatoire)</button></div>
        </div>
      )}
      {mode === "valide" && <ConfirmValidation montant={v.montant_demande_ht} label="retenue de garantie" onCancel={() => setMode(null)} onConfirm={go} busy={busy} />}
      {mode === "conteste" && (
        <div className="border-l-4 border-[#C0392B] bg-[#FEF2F2] p-3 space-y-2">
          <Motif value={motif} onChange={setMotif} testid="levee-conteste-motif" />
          <div className="flex gap-2 justify-end"><button className="btn-ghost" onClick={() => setMode(null)}>Revenir</button><button className="btn-danger" disabled={busy || motif.trim().length < 3} onClick={go} data-testid="levee-conteste-submit">Confirmer</button></div>
        </div>
      )}
    </Modal>
  );
};

/** Artisan : réponse à une validation partielle. */
export const ReponseArtisanModal = ({ v, onClose, onDone }) => {
  const [mode, setMode] = useState(null);
  const [motif, setMotif] = useState("");
  const [busy, go] = useGo(async () => {
    await api.post(`/versements/${v.id}/reponse-artisan`, mode === "accepte" ? { reponse: "accepte" } : { reponse: "rouvre", motif });
    toast.success(mode === "accepte" ? "Validation partielle acceptée — en attente de libération" : "Discussion rouverte — redéclarez après échange");
  }, onDone);
  return (
    <Modal title="Validation partielle proposée" onClose={onClose} testid="reponse-artisan-modal">
      <div className="bg-[#F5F6F7] rounded p-3 text-xs space-y-1">
        <div>Demandé : {v.pct_demande} % ({fmtEUR(v.montant_demande_ht)}) → constaté par le donneur d'ordre : <b className="text-base">{v.pct_valide} %</b> ({fmtEUR(v.montant_valide_ht)} HT)</div>
        <div className="italic">Motif : « {v.decision?.motif} »</div>
      </div>
      {mode === null && (
        <div className="space-y-3">
          <button className="btn-primary w-full py-3 justify-center" onClick={() => setMode("accepte")} data-testid="reponse-accepte-btn"><CheckCircle2 size={18} /> J'accepte {v.pct_valide} %</button>
          <div className="text-center"><button className="text-[#C0392B] underline text-sm" onClick={() => setMode("rouvre")} data-testid="reponse-rouvre-btn">Je rouvre la discussion (motif obligatoire)</button></div>
        </div>
      )}
      {mode === "accepte" && <ConfirmValidation montant={v.montant_valide_ht} label={`${v.pct_valide} %`} onCancel={() => setMode(null)} onConfirm={go} busy={busy} />}
      {mode === "rouvre" && (
        <div className="border-l-4 border-[#C0392B] bg-[#FEF2F2] p-3 space-y-2">
          <Motif value={motif} onChange={setMotif} testid="reponse-rouvre-motif" />
          <div className="flex gap-2 justify-end"><button className="btn-ghost" onClick={() => setMode(null)}>Revenir</button><button className="btn-danger" disabled={busy || motif.trim().length < 3} onClick={go} data-testid="reponse-rouvre-submit">Rouvrir</button></div>
        </div>
      )}
    </Modal>
  );
};
